import React, { useState } from 'react';
import { FaLightbulb, FaBrain, FaHandPaper } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import ModuleHeader from "../../moduleHeader";

const defaultPackageData = {
  user: {
    first_name: "Kullanıcı",
    last_name: ""
  },
  package: {
    name: "Anchoring Technique",
    description: "Kendinizi iyi hissettiğiniz bir anı hatırlayın ve bunu bir çapa olarak kullanın. Stres geldiğinde bu çapayı kullanarak sakinleşin.",
    image_url: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&q=80"
  }
};

export default function AnchoringTechnique({ id, moduleId, isOwner }) {
  const [step, setStep] = useState(1);
  const [memory, setMemory] = useState('');
  const [lightColor, setLightColor] = useState('#FFD700');
  const [isAnchored, setIsAnchored] = useState(false);
  const [packageData] = useState(defaultPackageData);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  const steps = [
    {
      id: 1,
      title: "İyi Bir Anı Hatırla",
      instruction: "Kendinizi iyi hissettiğiniz bir anı hatırlayın. Bu anı detaylıca düşünün.",
      placeholder: "Örnek: Deniz kenarında güneşin batışını izlediğim o güzel akşam..."
    },
    {
      id: 2,
      title: "Işığı Avucuna Sıkıştır",
      instruction: "Bu anıyı avucunuzda sıkışmış bir ışık olarak düşünün. Işığın rengini seçin.",
      placeholder: ""
    },
    {
      id: 3,
      title: "Çapayı Oluştur",
      instruction: "Artık çapanız hazır! Stres geldiğinde avucunuzu sıkın ve bu anıyı hatırlayın.",
      placeholder: ""
    }
  ];

  const handleNext = () => {
    if (step < steps.length) {
      setStep(step + 1);
      if (step === 2) {
        setIsAnchored(true);
      }
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      if (step === 3) {
        setIsAnchored(false);
      }
    }
  };

  const handleUseAnchor = () => {
    // Çapa kullanımı - animasyon göster
    alert("Avucunuzu sıkın ve o güzel anıyı hatırlayın. Işığın sıcaklığını hissedin...");
  };

  const handleRestart = () => {
    setStep(1);
    setMemory('');
    setLightColor('#FFD700');
    setIsAnchored(false);
  };

  const currentStepData = steps.find(s => s.id === step);

  const colorOptions = [
    { name: "Altın", value: "#FFD700" },
    { name: "Mavi", value: "#3B82F6" },
    { name: "Yeşil", value: "#10B981" },
    { name: "Mor", value: "#8B5CF6" },
    { name: "Pembe", value: "#EC4899" },
    { name: "Turuncu", value: "#F97316" }
  ];

  return (
    <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
      <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
        {/* ModuleHeader */}
        <div className="relative z-30 w-full mb-2">
          <ModuleHeader
            userName={packageData.user?.first_name + " " + packageData.user?.last_name}
            subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
            title={packageData.package?.name}
            description={packageData.package?.description}
            backgroundImage={packageData.package?.image_url}
            onExpandedChange={setIsHeaderExpanded}
            showToggle={true}
            autoHeight={true}
          />
        </div>

        <div className="w-full px-6 py-8 pb-24 flex-1">
          {!isAnchored ? (
            <>
              {/* Progress */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500">Adım {step} / {steps.length}</span>
                  <span className="text-xs text-gray-500">{Math.round((step / steps.length) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] h-2 rounded-full transition-all duration-500"
                    style={{ width: `${(step / steps.length) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Step Content */}
              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl p-6 mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">{currentStepData.title}</h2>
                <p className="text-gray-600 mb-6 text-center">{currentStepData.instruction}</p>

                {step === 1 && (
                  <div>
                    <textarea
                      value={memory}
                      onChange={(e) => setMemory(e.target.value)}
                      placeholder={currentStepData.placeholder}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3DA7B5] focus:border-transparent resize-none"
                      rows="6"
                    />
                  </div>
                )}

                {step === 2 && (
                  <div>
                    <div className="mb-6">
                      <p className="text-sm text-gray-600 mb-4 text-center">Işığın rengini seçin:</p>
                      <div className="grid grid-cols-3 gap-3">
                        {colorOptions.map((color) => (
                          <button
                            key={color.value}
                            onClick={() => setLightColor(color.value)}
                            className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                              lightColor === color.value 
                                ? 'border-gray-800 scale-110 shadow-lg' 
                                : 'border-gray-200 hover:border-gray-400'
                            }`}
                            style={{ backgroundColor: color.value }}
                          >
                            <div className="text-white text-xs font-semibold text-center">{color.name}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="text-center">
                      <div 
                        className="w-32 h-32 mx-auto rounded-full shadow-2xl animate-pulse"
                        style={{ backgroundColor: lightColor }}
                      ></div>
                      <p className="text-sm text-gray-500 mt-4">Seçtiğiniz ışık avucunuzda</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="flex gap-3">
                {step > 1 && (
                  <button
                    onClick={handleBack}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl py-3 font-semibold transition-all duration-200"
                  >
                    ← Geri
                  </button>
                )}
                <button
                  onClick={handleNext}
                  disabled={step === 1 && !memory.trim()}
                  className={`flex-1 bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] text-white rounded-xl py-3 font-semibold transition-all duration-200 ${
                    step === 1 && !memory.trim() 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:from-[#2F8894] hover:to-[#4A9BA6] transform hover:scale-[1.02]'
                  }`}
                >
                  {step === steps.length ? 'Tamamla ✓' : 'İleri →'}
                </button>
              </div>
            </>
          ) : (
            <div className="text-center animate-fadeIn">
              <div className="mb-6">
                <div 
                  className="w-48 h-48 mx-auto rounded-full shadow-2xl animate-pulse mb-4 flex items-center justify-center"
                  style={{ backgroundColor: lightColor }}
                >
                  <FaLightbulb className="text-6xl text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-2">
                  Çapanız Hazır! <HiSparkles className="text-2xl text-yellow-500" />
                </h2>
                <p className="text-gray-600 mb-6">
                  Stres geldiğinde avucunuzu sıkın ve bu güzel anıyı hatırlayın.
                </p>
              </div>

              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl p-6 mb-6 text-left">
                <h3 className="font-semibold text-gray-800 mb-3">Hafızanız:</h3>
                <p className="text-gray-700 italic">{memory}</p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleUseAnchor}
                  className="w-full bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] hover:from-[#2F8894] hover:to-[#4A9BA6] text-white rounded-xl py-4 font-semibold text-lg transition-all duration-200 transform hover:scale-[1.02] shadow-lg"
                >
                  Çapayı Kullan
                </button>
                <button
                  onClick={handleRestart}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl py-3 font-semibold transition-all duration-200"
                >
                  Yeni Çapa Oluştur
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

